import React, { useRef } from 'react';
import { Download, X, Printer } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';

const InvoiceView = ({ order, onClose, isAdmin = false }) => {
  const invoiceRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: invoiceRef,
    documentTitle: `Invoice-${order.invoice_number || order.id}`,
  });

  const handleDownloadPDF = async () => {
    const html2pdf = (await import('html2pdf.js')).default;
    html2pdf().set({
      margin: [4, 4, 4, 4],
      filename: `Invoice-${order.invoice_number || order.id}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a5', orientation: 'landscape' },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    }).from(invoiceRef.current).save();
  };

  const items = order.items || [];

  const S = {
    table: { width: '100%', borderCollapse: 'collapse', border: '2px solid #000', fontFamily: 'Arial, sans-serif', fontSize: '13px', color: '#000', tableLayout: 'fixed' },
    cellL: { border: '1.5px solid #000', padding: '8px 10px', verticalAlign: 'top', width: '50%' },
    cellR: { border: '1.5px solid #000', padding: '8px 10px', verticalAlign: 'top', width: '50%' },
    cellInv: { border: '1.5px solid #000', padding: '8px 10px', width: '50%' },
    cellDate: { border: '1.5px solid #000', padding: '8px 10px', width: '25%' },
    cellTime: { border: '1.5px solid #000', padding: '8px 10px', width: '25%' },
    hSr: { border: '1.5px solid #000', padding: '6px 4px', textAlign: 'center', fontWeight: 'bold', width: '7%', fontSize: '12px' },
    hName: { border: '1.5px solid #000', padding: '6px 4px', textAlign: 'center', fontWeight: 'bold', width: '43%', fontSize: '12px' },
    hCat: { border: '1.5px solid #000', padding: '6px 4px', textAlign: 'center', fontWeight: 'bold', width: '25%', fontSize: '12px' },
    hQty: { border: '1.5px solid #000', padding: '6px 4px', textAlign: 'center', fontWeight: 'bold', width: '12.5%', fontSize: '12px' },
    hUnit: { border: '1.5px solid #000', padding: '6px 4px', textAlign: 'center', fontWeight: 'bold', width: '12.5%', fontSize: '12px' },
    rSr: { border: '1.5px solid #000', padding: '5px 4px', textAlign: 'center', fontWeight: 'bold' },
    rName: { border: '1.5px solid #000', padding: '5px 8px', wordBreak: 'break-word', overflow: 'hidden' },
    rCat: { border: '1.5px solid #000', padding: '5px 4px', textAlign: 'center', wordBreak: 'break-word', overflow: 'hidden' },
    rQty: { border: '1.5px solid #000', padding: '5px 4px', textAlign: 'center', fontWeight: 'bold' },
    rUnit: { border: '1.5px solid #000', padding: '5px 4px', textAlign: 'center' },
    bold: { fontWeight: 'bold' },
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-4 px-2 no-print-overlay" data-testid="invoice-modal" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-[750px] shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Buttons */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 no-print">
          <span className="text-sm font-semibold text-[#1A3626]">Invoice</span>
          <div className="flex items-center gap-2">
            {isAdmin && <button onClick={handlePrint} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1A3626] text-white rounded-md text-xs font-medium" data-testid="print-invoice-btn"><Printer className="h-3.5 w-3.5" />Print</button>}
            <button onClick={handleDownloadPDF} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#C05A3E] text-white rounded-md text-xs font-medium" data-testid="download-pdf-btn"><Download className="h-3.5 w-3.5" />PDF</button>
            <button onClick={onClose} className="ml-1 p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100" data-testid="close-invoice-btn"><X className="h-4 w-4" /></button>
          </div>
        </div>

        {/* Invoice Content */}
        <div ref={invoiceRef} className="invoice-print-root" style={{ padding: '16px', fontFamily: 'Arial, sans-serif', color: '#000', background: '#fff' }}>
          <table style={S.table}>
            <colgroup>
              <col style={{ width: '7%' }} />
              <col style={{ width: '43%' }} />
              <col style={{ width: '25%' }} />
              <col style={{ width: '12.5%' }} />
              <col style={{ width: '12.5%' }} />
            </colgroup>
            <tbody>
              {/* Row 1: Name/Address | Self/Transport + Phone */}
              <tr>
                <td colSpan={2} style={S.cellL}>
                  <div style={{ marginBottom: '6px' }}><span style={S.bold}>NAME :</span>&nbsp;&nbsp;&nbsp;{order.customer_name}</div>
                  <div><span style={S.bold}>ADDRESS :</span>&nbsp;&nbsp;&nbsp;{order.address || '-'}</div>
                </td>
                <td colSpan={3} style={S.cellR}>
                  <div style={{ marginBottom: '6px' }}><span style={S.bold}>SELF / TRANSPORT :</span>&nbsp;&nbsp;&nbsp;{order.transport_mode || 'Self'}</div>
                  <div><span style={S.bold}>PHONE NO. :</span>&nbsp;&nbsp;&nbsp;{order.phone}</div>
                </td>
              </tr>
              {/* Row 2: Invoice No | Date | Time */}
              <tr>
                <td colSpan={2} style={S.cellInv}>
                  <span style={S.bold}>INVOICE NO. :</span>&nbsp;&nbsp;{order.invoice_number || 'N/A'}
                </td>
                <td style={S.cellDate}>
                  <span style={S.bold}>DATE :</span>&nbsp;&nbsp;{order.order_date_ist || 'N/A'}
                </td>
                <td colSpan={2} style={S.cellTime}>
                  <span style={S.bold}>TIME :</span>&nbsp;&nbsp;{order.order_time_ist || 'N/A'}
                </td>
              </tr>
              {/* Row 3: Table Header */}
              <tr>
                <td style={S.hSr}>SR. NO.</td>
                <td style={S.hName}>PRODUCT NAME</td>
                <td style={S.hCat}>CATEGORY</td>
                <td style={S.hQty}>QTY</td>
                <td style={S.hUnit}>UNIT</td>
              </tr>
              {/* Product rows */}
              {items.map((item, i) => (
                <tr key={i}>
                  <td style={S.rSr}>{i + 1}</td>
                  <td style={S.rName}>{item.product_name}</td>
                  <td style={S.rCat}>{item.category_name || ''}</td>
                  <td style={S.rQty}>{item.quantity}</td>
                  <td style={S.rUnit}>{item.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InvoiceView;
